import { Markdown } from '@cadstrom/sigma-engine-shared';

import type { ModelWithNetStats } from '@cadstrom/sigma-engine-shared';
import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import type { Node as ProseNode } from '@milkdown/kit/prose/model';
import type { EditorView, NodeViewConstructor } from '@milkdown/kit/prose/view';
import type { MarkdownNode } from '@milkdown/kit/transformer';
import type { ReactNode } from 'react';

export type MarkdownReferenceKind = 'module' | 'net';

export type MarkdownReferenceSuggestion = {
	kind: MarkdownReferenceKind;
	value: string;
	label: string;
	detail?: string;
};

export type ReferenceSuggestionTrigger = {
	/** The query text typed after the marker, excluding the marker itself. */
	query: string;
	/** Document position of the marker character. */
	from: number;
	/** Document position of the caret, immediately after the query. */
	to: number;
};

export type ReferenceChipView = {
	id: number;
	kind: MarkdownReferenceKind;
	value: string;
	attrs: Record<string, string>;
	dom: HTMLElement;
};

export type ReferenceChipViewController = {
	/** Mounts a React chip portal into a ProseMirror node view host element. */
	mount: (kind: MarkdownReferenceKind, value: string, attrs: Record<string, string>, dom: HTMLElement) => number;
	/** Updates a mounted chip portal when the backing ProseMirror node attrs change. */
	update: (id: number, value: string, attrs: Record<string, string>) => void;
	/** Removes a chip portal when ProseMirror destroys its node view. */
	unmount: (id: number) => void;
};

export type MarkdownReferenceEditorPlugin = {
	kind: MarkdownReferenceKind;
	marker: string;
	nodeName: string;
	attrName: string;
	/** Regex character-class body matching the characters allowed in an in-progress query. */
	suggestionQueryCharClass: string;
	/** Hint shown as the first row of the suggestion menu before the user types a query. */
	suggestionHint: string;
	schema: MilkdownPlugin | MilkdownPlugin[];
	/** Creates the Milkdown node view plugin that renders reference atoms. */
	createViewPlugin: (controller: ReferenceChipViewController) => MilkdownPlugin;
	/** Returns suggestions for the editor's reference autocomplete menu. */
	getSuggestions: (coreModel: ModelWithNetStats, query: string) => MarkdownReferenceSuggestion[];
	/** Renders the React chip used inside the reference atom node view. */
	renderChip: (value: string, attrs: Record<string, string>) => ReactNode;
	/**
	 * Handles a space keypress while this plugin's suggestion trigger is active.
	 *
	 * @param view - The editor view.
	 * @param trigger - The active suggestion trigger.
	 * @returns True if the space was consumed (and should be prevented), false to let it through.
	 */
	handleTriggerSpace?: (view: EditorView, trigger: ReferenceSuggestionTrigger) => boolean;
};

/**
 * Builds a normalized suggestion object for module and net reference completion menus.
 */
export function createReferenceSuggestion(
	kind: MarkdownReferenceKind,
	value: string,
	label: string,
	detail?: string,
): MarkdownReferenceSuggestion {
	return detail === undefined ? { kind, value, label } : { kind, value, label, detail };
}

/**
 * Creates a ProseMirror node view that delegates rendering of a reference atom to a React portal.
 */
export function createReferenceChipNodeView(
	nodeName: string,
	attrName: string,
	kind: MarkdownReferenceKind,
	controller: ReferenceChipViewController,
): NodeViewConstructor {
	return (node) => {
		const dom = document.createElement('span');
		dom.dataset['markdownReferenceChipView'] = kind;
		dom.contentEditable = 'false';
		dom.className = 'inline-flex align-baseline';

		let value = getReferenceNodeAttr(node, attrName);
		let attrs = node.attrs;
		const id = controller.mount(kind, value, attrs, dom);

		return {
			dom,
			update: (nextNode) => {
				if (nextNode.type.name !== nodeName) {
					return false;
				}

				const nextValue = getReferenceNodeAttr(nextNode, attrName);
				if (nextValue !== value || nextNode.attrs !== attrs) {
					value = nextValue;
					attrs = nextNode.attrs;
					controller.update(id, nextValue, attrs);
				}

				return true;
			},
			destroy: () => controller.unmount(id),
			ignoreMutation: () => true,
			stopEvent: () => true,
		};
	};
}

/**
 * Checks whether a Milkdown markdown node is one of this plugin family's directive nodes.
 */
export function isReferenceDirectiveNode(node: MarkdownNode, name: string) {
	return (
		(node.type === (Markdown.NodeTypes.TextDirective as string) ||
			node.type === (Markdown.NodeTypes.LeafDirective as string) ||
			node.type === (Markdown.NodeTypes.ContainerDirective as string)) &&
		node['name'] === name
	);
}

/**
 * Checks whether a reference directive can be rendered inline without wrapping it in a paragraph.
 */
export function isInlineReferenceDirectiveNode(node: MarkdownNode) {
	return node.type === (Markdown.NodeTypes.TextDirective as string);
}

/**
 * Extracts a module or net reference value from directive content/title, falling back to attributes.
 */
export function getDirectiveReferenceValue(node: MarkdownNode, attributeName: string) {
	const content = getDirectiveContent(node);
	if (content !== undefined) {
		return content;
	}

	const attributes = node['attributes'];
	if (attributes !== null && typeof attributes === 'object' && !Array.isArray(attributes)) {
		const value = (attributes as Record<string, unknown>)[attributeName];
		if (typeof value === 'string' && value.trim() !== '') {
			return value;
		}
	}

	return undefined;
}

/**
 * Reads the string reference value stored on a ProseMirror reference atom.
 */
function getReferenceNodeAttr(node: ProseNode, attrName: string) {
	const value: unknown = node.attrs[attrName];
	return typeof value === 'string' ? value : '';
}

/**
 * Extracts user-visible directive content from text, leaf, or container directive nodes.
 */
function getDirectiveContent(node: MarkdownNode) {
	const nodes =
		node.type === 'containerDirective' ? getContainerDirectiveTitleNodes(node)
		: Array.isArray(node.children) ? node.children
		: [];
	const value = collectMarkdownText(nodes).trim();

	return value === '' ? undefined : value;
}

/**
 * Returns the title paragraph children for container directives created with `:::name[title]`.
 */
function getContainerDirectiveTitleNodes(node: MarkdownNode): MarkdownNode[] {
	const firstChild = node.children?.[0];
	if (firstChild?.type !== 'paragraph') {
		return [];
	}

	const data = firstChild.data;
	if (typeof data !== 'object' || Array.isArray(data)) {
		return [];
	}

	if ((data as Record<string, unknown>)['directiveLabel'] !== true) {
		return [];
	}

	return firstChild.children ?? [];
}

/**
 * Concatenates text from markdown nodes while ignoring non-text formatting wrappers.
 */
function collectMarkdownText(nodes: readonly MarkdownNode[]): string {
	return nodes
		.map((node) => {
			if (typeof node['value'] === 'string') {
				return node['value'];
			}

			return Array.isArray(node.children) ? collectMarkdownText(node.children) : '';
		})
		.join('');
}
